// src/components/MultiProductDialog.jsx
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { X, Plus, Trash2, Package, ShoppingBag, Check } from 'lucide-react'
import ProductDropdown from './ProductDropdown'

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSave: (items) => void       // items = [{ sku, title, quantity }]
 *  - userId: number
 *  - initialItems?: same shape
 *  - canAddProduct?: boolean       // pass !!(user && !user.provider_id)
 */
const MultiProductDialog = ({
  open,
  onClose,
  onSave,
  userId,
  initialItems = [],
  canAddProduct = false,
}) => {
  const baseUrl = process.env.REACT_APP_API_URL || ''

  // Brand colors
  const brand = {
    primary: '#8B4513',
    accentGold: '#D4AF37',
    accentGreen: '#2E7D32'
  }

  // rows in the package
  const [rows, setRows] = useState(
    initialItems.length ? initialItems : [{ sku: '', title: '', quantity: 1 }]
  )
  const [refreshFlag, setRefreshFlag] = useState(0)

  // --- Nested "Add Product" dialog state ---
  const [addOpen, setAddOpen] = useState(false)
  const [activeRow, setActiveRow] = useState(null)
  const [newSku, setNewSku] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [categories, setCategories] = useState([])
  const [loadingSave, setLoadingSave] = useState(false)

  // Reset rows on open
  useEffect(() => {
    if (open) {
      setRows(initialItems.length ? initialItems : [{ sku: '', title: '', quantity: 1 }])
      setRefreshFlag(f => f + 1)
    }
  }, [open, initialItems])

  // Prefetch categories when dialog opens
  useEffect(() => {
    if (!open || !canAddProduct) return
    axios.get(`${baseUrl}/api/categories`)
      .then(res => setCategories(res.data || []))
      .catch(console.error)
  }, [open, canAddProduct, baseUrl])

  const updateRow = (idx, patch) => {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  }

  const addRow = () =>
    setRows(prev => [...prev, { sku: '', title: '', quantity: 1 }])

  const removeRow = (idx) =>
    setRows(prev => prev.filter((_, i) => i !== idx))

  const handleSave = () => {
    const cleaned = rows
      .filter(r => r.sku && Number(r.quantity) > 0)
      .map(r => ({
        sku: r.sku,
        title: r.title,
        quantity: Math.max(1, parseInt(r.quantity || 1, 10)),
      }))
    onSave(cleaned)
    onClose()
  }

  // ---------- Add Product dialog handlers ----------
  const openAddProductForRow = (rowIdx) => {
    setActiveRow(rowIdx)
    setNewSku('')
    setNewTitle('')
    setNewPrice('')
    setNewCategory('')
    setAddOpen(true)
  }
  const closeAdd = () => setAddOpen(false)

  const handleSaveNewProduct = async () => {
    if (!newSku.trim() || !newTitle.trim() || newPrice === '') {
      alert('SKU, Título y Precio son obligatorios')
      return
    }
    setLoadingSave(true)
    try {
      const payload = {
        sku: newSku.trim(),
        title: newTitle.trim(),
        price: Number(newPrice),
        user_id: userId,
        category_ids: newCategory ? [Number(newCategory)] : []
      }
      const res = await axios.post(`${baseUrl}/api/create_product`, payload)

      const saved = res.data?.product || {
        sku: newSku.trim(),
        title: newTitle.trim()
      }

      setRefreshFlag(f => f + 1)

      setRows(prev => {
        let next = [...prev]
        let idx = activeRow

        if (idx == null || idx < 0 || idx >= next.length) {
          next = [...next, { sku: '', title: '', quantity: 1 }]
          idx = next.length - 1
        }
        next[idx] = { ...next[idx], sku: saved.sku, title: saved.title }
        return next
      })
      setAddOpen(false)
    } catch (err) {
      console.error(err)
      alert(err?.response?.data?.error || 'Error al crear el producto')
    } finally {
      setLoadingSave(false)
    }
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[70] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[90dvh] min-h-0 overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-6 sm:py-5 border-b border-slate-200 shrink-0">
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-xl"
                style={{ backgroundColor: `${brand.primary}15` }}
              >
                <ShoppingBag className="h-6 w-6" style={{ color: brand.primary }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Productos del paquete</h2>
                <p className="text-sm text-slate-500">Selecciona los productos y cantidades</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-6 sm:py-5">
            {canAddProduct && (
              <button
                onClick={() => openAddProductForRow(Math.max(0, rows.findIndex(r => !r.sku)))}
                className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-dashed hover:border-solid transition-all flex items-center justify-center gap-2 text-sm font-medium"
                style={{ 
                  borderColor: brand.accentGold,
                  color: brand.accentGold 
                }}
              >
                <Plus className="h-4 w-4" />
                Crear nuevo producto
              </button>
            )}

            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-50 rounded-xl p-4 border-2 border-slate-100 hover:border-slate-200 transition-all"
                >
                  <div className="flex items-start gap-3">
                    {/* Product Icon */}
                    <div 
                      className="mt-1 p-2 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: `${brand.accentGreen}15` }}
                    >
                      <Package className="h-4 w-4" style={{ color: brand.accentGreen }} />
                    </div>

                    {/* Product Selection */}
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs font-semibold text-slate-700 mb-2">
                        Producto {idx + 1}
                      </label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <ProductDropdown
                            userId={userId}
                            refreshFlag={refreshFlag}
                            value={row.sku ? { sku: row.sku, title: row.title } : null}
                            onSelect={prod => {
                              if (prod) updateRow(idx, { sku: prod.sku, title: prod.title })
                              else updateRow(idx, { sku: '', title: '' })
                            }}
                          />
                        </div>
                        {canAddProduct && (
                          <button
                            onClick={() => openAddProductForRow(idx)}
                            className="p-2 rounded-lg hover:bg-white transition-colors"
                            style={{ color: brand.accentGold }}
                            title="Crear producto"
                          >
                            <Plus className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className="w-24 flex-shrink-0">
                      <label className="block text-xs font-semibold text-slate-700 mb-2">
                        Cantidad
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={row.quantity}
                        onChange={e => updateRow(idx, { quantity: e.target.value })}
                        className="w-full rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-slate-400 transition-all text-center"
                      />
                    </div>

                    {/* Delete */}
                    <button
                      onClick={() => removeRow(idx)}
                      className="mt-7 p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500"
                      title="Eliminar"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Another Product Button */}
            <button
              onClick={addRow}
              className="mt-4 w-full px-4 py-3 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-sm font-medium text-slate-600"
            >
              <Plus className="h-4 w-4" />
              Agregar otro producto
            </button>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50 rounded-b-2xl">
            <p className="text-sm text-slate-600">
              {rows.filter(r => r.sku).length} producto(s) seleccionado(s)
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-slate-100 transition-all text-sm font-medium text-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2.5 rounded-xl text-white transition-all text-sm font-medium flex items-center gap-2 shadow-lg"
                style={{ 
                  backgroundColor: brand.primary,
                  boxShadow: `0 4px 12px ${brand.primary}40`
                }}
              >
                <Check className="h-4 w-4" />
                Guardar productos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Nested: Add Product Modal */}
      {canAddProduct && addOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 z-[80] backdrop-blur-sm"
            onClick={closeAdd}
          />
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-2 sm:p-4">
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div 
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: `${brand.accentGold}15` }}
                  >
                    <Plus className="h-5 w-5" style={{ color: brand.accentGold }} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800">Crear nuevo producto</h3>
                </div>
                <button
                  onClick={closeAdd}
                  disabled={loadingSave}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    SKU *
                  </label>
                  <input
                    type="text"
                    value={newSku}
                    onChange={e => setNewSku(e.target.value)}
                    placeholder="Ej: PROD-001"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-slate-400 transition-all"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                    placeholder="Nombre del producto"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-slate-400 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Precio (S/.) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPrice}
                    onChange={e => setNewPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-slate-400 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Categoría
                  </label>
                  <select
                    value={newCategory}
                    onChange={e => setNewCategory(e.target.value)}
                    className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-slate-400 transition-all"
                  >
                    <option value="">Sin categoría</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-2xl">
                <button
                  onClick={closeAdd}
                  disabled={loadingSave}
                  className="px-5 py-2.5 rounded-xl border-2 border-slate-200 hover:bg-white transition-all text-sm font-medium text-slate-700 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveNewProduct}
                  disabled={loadingSave}
                  className="px-5 py-2.5 rounded-xl text-white transition-all text-sm font-medium disabled:opacity-50 shadow-lg"
                  style={{ 
                    backgroundColor: brand.accentGold,
                    boxShadow: `0 4px 12px ${brand.accentGold}40`
                  }}
                >
                  {loadingSave ? 'Guardando…' : 'Crear producto'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default MultiProductDialog
